import type { CorridorDefinition } from "@/types/corridor";
import type { RoutePageApiResponse, RoutePageCorridorJson } from "@/types/routePageApi";

import hydVja from "@/data/api-mock/routes/hyderabad-vijayawada.json";
import hydGnt from "@/data/api-mock/routes/hyderabad-guntur.json";

const BUNDLED: Record<string, RoutePageApiResponse> = {
  "hyderabad-vijayawada": hydVja as unknown as RoutePageApiResponse,
  "hyderabad-guntur": hydGnt as unknown as RoutePageApiResponse,
};

function mockApiBaseUrl(): string {
  const base = import.meta.env.BASE_URL ?? "/";
  return base.endsWith("/") ? base : `${base}/`;
}

/**
 * Simulated API: short delay, then JSON (optional `public/api-mock/routes/{slug}.json`, else bundled).
 */
export async function fetchRoutePageMock(slug: string): Promise<RoutePageApiResponse | null> {
  await new Promise((r) => setTimeout(r, 160 + Math.random() * 140));
  try {
    const res = await fetch(`${mockApiBaseUrl()}api-mock/routes/${encodeURIComponent(slug)}.json`, {
      cache: "no-store",
    });
    if (res.ok) {
      const body = (await res.json()) as RoutePageApiResponse;
      if (body?.ok && body.corridor && body.rides) return body;
    }
  } catch {
    /* use bundled */
  }
  return BUNDLED[slug] ?? null;
}

/**
 * Merges JSON corridor into URL-resolved row (keeps `cityA` / `cityB` / `slug` / zone order).
 * `assetSource` supplies `heroVideoSrc` when JSON omits or nulls it.
 */
export function mergeApiCorridorIntoResolved(
  resolved: CorridorDefinition,
  json: RoutePageCorridorJson,
  assetSource: CorridorDefinition
): CorridorDefinition {
  const heroVideoSrc =
    json.heroVideoSrc != null && String(json.heroVideoSrc).trim() !== ""
      ? (String(json.heroVideoSrc) as CorridorDefinition["heroVideoSrc"])
      : assetSource.heroVideoSrc;

  return {
    ...resolved,
    stats: {
      ...resolved.stats,
      ...json.stats,
      zonesA: resolved.stats.zonesA,
      zonesB: resolved.stats.zonesB,
    },
    heroImageSrc: json.heroImageSrc,
    heroImageAlt: json.heroImageAlt,
    seoOgImage: json.seoOgImage,
    heroVideoSrc,
    midCta: json.midCta,
    localLife: json.localLife,
    problemSolution: json.problemSolution,
    spots: json.spots,
    happenings: json.happenings,
    testimonials: json.testimonials,
    faqs: json.faqs,
  };
}
